import { ReactNode, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router";
import { NavigationBarMenu } from "../../components/navigation/NavigationBar";
import { Icon } from "../../components";
import { useMediaQuery } from "usehooks-ts";
import { twMerge } from "tailwind-merge";

import * as ROUTES from "../../constants/routes";
import { useRemoteFeatureFlags } from "../../hooks/useRemoteFeatureFlags";
import { useDeliveryZoneMode } from "../../hooks/useDeliveryZoneMode";
import {
  Anchor,
  InputContainer,
  InputOption,
  InputSelect,
  H4,
} from "@sellar-io/components";

export const Delivery = ({ children }: { children: ReactNode }) => {
  const isDesktop = useMediaQuery("(min-width: 1200px)");

  return (
    <>
      <NavigationBarMenu title="Delivery" borderBottom={false} />
      <div className="mx-auto flex w-full max-w-[920px] min-w-0 items-center gap-2">
        <div
          className={twMerge(
            "mb-24 flex w-full min-w-0 flex-col gap-4 px-4",
            isDesktop ? "mt-10" : "mt-2",
          )}
        >
          <div className="flex flex-col gap-1 border-b border-zinc-300 pb-4">
            <H4 className="font-semibold">Delivery</H4>
            <p>
              Set up your delivery information to provide your customers with
              accurate fulfilment expectations. For more information or help
              setting up your delivery, visit our{" "}
              <Anchor
                className="inline-flex items-center gap-1 text-blue-600 underline"
                href="https://intercom.help/sellario/en/articles/16971829-supplier-setup-delivery-definitions-and-faq"
                target="_blank"
                rel="noopener noreferrer"
              >
                help centre
                <Icon icon="ARROW_TOP_RIGHT" size={0.6} />
              </Anchor>
              .
            </p>
          </div>

          <Tabs />
          {children}
        </div>
      </div>
    </>
  );
};

export const Tabs = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const isMobile = useMediaQuery("(max-width: 700px)");

  const isUnbundlingEnabled = useRemoteFeatureFlags().isActive(
    "unbundlingReleaseFinal",
  );
  const deliveryZoneMode = useDeliveryZoneMode();

  useEffect(() => {
    if (
      deliveryZoneMode.isBasicMode &&
      location.pathname === ROUTES.DELIVERY_ZONES
    ) {
      navigate(ROUTES.DELIVERY_METHODS, { replace: true });
    }
  }, [deliveryZoneMode.isBasicMode, location.pathname, navigate]);

  const tabs = useMemo(
    () => [
      { label: "Methods", route: ROUTES.DELIVERY_METHODS },
      ...(isUnbundlingEnabled && deliveryZoneMode.isAdvancedMode
        ? [
            {
              label: "Zones",
              route: ROUTES.DELIVERY_ZONES,
            },
          ]
        : []),
      { label: "Schedules", route: ROUTES.DELIVERY_SCHEDULES },
      ...(isUnbundlingEnabled
        ? [
            {
              label: "Sellar Distribution",
              route: ROUTES.SUPPLIER_DISTRIBUTION,
              className: "max-w-42",
            },
          ]
        : []),
      {
        label: (
          <span className="flex items-center justify-center gap-1">
            <Icon icon="COG" size={0.7} />
            Settings
          </span>
        ),
        labelMobile: "Settings",
        route: ROUTES.DELIVERY_SETTINGS_V2,
        className: "ml-auto",
      },
    ],
    [deliveryZoneMode.isAdvancedMode, isUnbundlingEnabled],
  );

  return isMobile ? (
    <InputContainer className="w-full">
      <InputSelect
        name="tabs"
        aria-label="Delivery section"
        value={tabs.findIndex((tab) => location.pathname === tab.route)}
        onChange={(e) => {
          const route = tabs[parseInt(e.target.value, 10)]?.route;
          if (!route) return;
          navigate(route);
        }}
      >
        {tabs.map((tab, idx) => (
          <InputOption key={tab.route} value={idx}>
            {tab.labelMobile || tab.label}
          </InputOption>
        ))}
      </InputSelect>
    </InputContainer>
  ) : (
    <div className="mb-4 flex w-full items-center border-b border-zinc-300">
      {tabs.map((tab) => (
        <button
          type="button"
          aria-current={location.pathname === tab.route ? "page" : undefined}
          key={tab.route}
          onClick={() => navigate(tab.route)}
          className={twMerge(
            "max-w-38 grow cursor-pointer border-b-4 border-transparent px-2 py-4 text-center font-semibold text-zinc-500 hover:bg-zinc-100",
            location.pathname === tab.route && "border-zinc-800 text-zinc-800",
            tab.className,
          )}
        >
          <span>{tab.label}</span>
        </button>
      ))}
    </div>
  );
};
